import { AgGridReact } from '@mint-lib/ag-grid/react';
import { useCallback, useMemo, useRef, type CSSProperties } from 'react';

import type {
  ColDef,
  GridReadyEvent,
  ValueFormatterParams,
} from '@mint-lib/ag-grid/community';
import {
  useDateFormatter,
  useNumberFormatter,
} from '@mint-lib/formatting-hooks';

import './grid.css';

import type { Chat } from '@mint-sdk/api-ai-assistant';
import { AllCommunityModule } from 'ag-grid-community';

import { formatLabel } from '../../helpers/charts.js';
import { dataTableValueFormatter } from './helpers.js';
import { useGrabScroll } from './useGrabScroll.jsx';

const modules = [AllCommunityModule];

type Props = {
  dataframeData: Chat.AiChatMessageRecordType['message']['additionalMetadata']['dataframe'];
};

const AiDataGrid = ({ dataframeData = [] }: Props) => {
  const gridRef = useRef<HTMLDivElement | null>(null);
  const numberFormatter = useNumberFormatter();
  const dateFormatter = useDateFormatter();

  const { grabScroll } = useGrabScroll({
    ref: gridRef,
  });

  const onGridReady = useCallback(
    (params: GridReadyEvent) => {
      params.api.autoSizeAllColumns({
        skipHeader: false, // Removes truncation on scrollable tables
      });
      grabScroll();
    },
    [grabScroll],
  );

  const isSmallColsTable = Object.keys(dataframeData[0]).length <= 3;

  const defaultColDef = useMemo<ColDef>(() => {
    return {
      flex: isSmallColsTable ? 1 : undefined,
      sortable: true,
      wrapText: true,
      autoHeight: true,
      filter: false,
      resizable: false,
      suppressHeaderMenuButton: true,
    };
  }, [isSmallColsTable]);

  const valueFormatter = useCallback(
    (params: ValueFormatterParams) =>
      dataTableValueFormatter(params.value, numberFormatter, dateFormatter),
    [numberFormatter, dateFormatter],
  );

  const generateColumns = useCallback(
    (data: typeof dataframeData) => {
      if (!data.length) {
        return [];
      }

      return Object.keys(data[0]).map((key) => ({
        field: key,
        headerName: formatLabel({
          label: key,
        }),
        valueFormatter,
      }));
    },
    [valueFormatter],
  );

  const columnDefs = useMemo<ColDef[]>(
    () => generateColumns(dataframeData),
    [dataframeData, generateColumns],
  );

  return (
    // domLayout="autoHeight" makes the grid grow to fit its rows; the wrapper's
    // max-h-110 caps it and scrolls once the content exceeds the max height.
    <div
      aria-label="ai-data-grid-wrapper"
      ref={gridRef}
      className="flex max-h-110 min-h-0 w-full flex-col overflow-auto"
      style={{ '--row-count': dataframeData.length } as CSSProperties}
    >
      <AgGridReact
        onGridReady={onGridReady} // Ref should be defined
        className="ai-data-grid"
        domLayout="autoHeight"
        // Native viewport scrollbars are revealed in grid.css
        suppressHorizontalScroll
        rowData={dataframeData}
        columnDefs={columnDefs}
        defaultColDef={defaultColDef}
        suppressMovableColumns
        suppressContextMenu
        suppressColumnMoveAnimation
        suppressCellFocus
        suppressHeaderFocus
        enableCellTextSelection
        modules={modules}
      />
    </div>
  );
};

export { AiDataGrid };
